import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewChildren,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { RoleBasedParamModel } from '../../../core/models/role-based-param.model';
import { ThenableEventEmitter } from '../../utils/thenable-event';
import { CustomFilter } from './models/custom-filter.model';
import { FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { AutoCompleteRefModel } from '../../../../app/core/models/autocomplete-ref.model';
import { SelectAutocompleteService } from '../../../../app/core/services/select-autocomplete.service';
import { SelectAutocompleteComponent } from '../mat-select-autocomplete/select-autocomplete.component';
import { MatExpansionPanel } from '@angular/material/expansion';
import { CustomFilterButton } from './models/custom-filter-button.model';
import { UtilsService } from '../../../../app/core/services/utils.service';

@Component({
	selector: 'ag-custom-filters',
	templateUrl: './custom-filters.component.html',
	styleUrls: ['./custom-filters.component.scss'],
	standalone: false
})
export class CustomFiltersComponent implements OnInit, AfterViewInit {
	subscription = new Subscription();
	@ViewChildren(SelectAutocompleteComponent) autocompleteDropdowns: SelectAutocompleteComponent[];
	@ViewChild(MatExpansionPanel) matExpansionPanel: MatExpansionPanel;

	/** Input used to provide an array of configurable filters to render the component */
	@Input()
	public filters: CustomFilter[] = [];
	/** Input used to provide group validators to be applied too the form */
	@Input()
	public groupValidators: ValidatorFn[] = [];
	/** Input used to provide the parent component */
	@Input()
	public instance: Component;
	/** Input used to activate the export feature */
	@Input()
	public hasExport: boolean = false;
	/** Input used to customise the search button label */
	@Input()
	public searchLabel: string = 'Search';
	/** Input used to customise the search button size */
	@Input()
	public searchFlexSize: string = '15%';
	/** Input used to provide custom buttons */
	@Input()
	public customButtons: CustomFilterButton[] = [];
	/** Input used to instantiate the component with a closed expansion panel */
	@Input()
	public isExpansionOpen = true;
	/** Input used to disable the whole filter section */
	@Input()
	public notEditable = false;
	/** Input used to customise the filter section name */
	@Input()
	public filterSectionTitle = 'Filters';
	/** Input used to disable the automatic close of the expansion panel on an action */
	@Input()
	public closeExpansionOnAction = true;
	/** Input used to disable the search output on a clear action */
	@Input()
	public emittableClear = true;
	/** Input used to disable manually closing the expansion panel */
	@Input()
	public hideExpansionToggle = false;

	/** Event emitted on search button press if the form is valid */
	@Output()
	public search = new ThenableEventEmitter<any>();
	/** Event emitted on export button press if the form is valid */
	@Output()
	public export = new ThenableEventEmitter<any>();
	/** Event emitted if the form is invalid */
	@Output()
	public invalidForm = new ThenableEventEmitter<any>();
	/** Event emitted on any filter change */
	@Output()
	public filterChange = new ThenableEventEmitter<any>();
	/** Event emitted on any filter clear */
	@Output()
	public filterClear = new ThenableEventEmitter<any>();

	form: FormGroup;

	public refModels: Map<string, AutoCompleteRefModel> = new Map();

	constructor(
		private readonly cdref: ChangeDetectorRef,
		private readonly fb: FormBuilder,
		private readonly autocompleteService: SelectAutocompleteService,
	) {
		this.form = this.fb.group({});
	}

	/** Function used to set values that will remain on the filters no matter what ( even on clear ) */
	setDefaultValues() {
		this.filters.map(filter => {
			if (filter.type === 'date') {
				if (filter.datePickerRange) {
					if (filter.defaultValues?.length == 1) {
						this.form.controls[filter.id + 'Start'].setValue(filter.defaultValues[0]);
					} else if (filter.defaultValues?.length == 2) {
						this.form.controls[filter.id + 'Start'].setValue(filter.defaultValues[0]);
						this.form.controls[filter.id + 'End'].setValue(filter.defaultValues[1]);
					}
				} else {
					if (filter.defaultValues?.length == 1) {
						this.form.controls[filter.id].setValue(filter.defaultValues[0]);
					}
				}
			} else if (['select', 'autocomplete'].includes(filter.type)) {
				if (filter.defaultValues?.length) {
					if (filter.multipleSelect) {
						this.form.controls[filter.id].setValue(filter.defaultValues);
					} else {
						this.form.controls[filter.id].setValue(filter.defaultValues[0]);
					}
				}
			} else {
				if (filter.defaultValues?.length == 1) {
					this.form.controls[filter.id].setValue(filter.defaultValues[0]);
				}
			}
		})
	}

	/** Function used to set values to instantiate the filters with, but can be clearable */
	setPreservedValues() {
		this.filters.map(filter => {
			if (filter.type === 'date') {
				if (filter.datePickerRange) {
					if (filter.preservedValues?.length == 1) {
						this.form.controls[filter.id + 'Start'].setValue(filter.preservedValues[0]);
					} else if (filter.preservedValues?.length == 2) {
						this.form.controls[filter.id + 'Start'].setValue(filter.preservedValues[0]);
						this.form.controls[filter.id + 'End'].setValue(filter.preservedValues[1]);
					}
				} else {
					if (filter.preservedValues?.length == 1) {
						this.form.controls[filter.id].setValue(filter.preservedValues[0]);
					}
				}
			} else if (['select', 'autocomplete'].includes(filter.type)) {
				if (filter.preservedValues?.length) {
					if (filter.multipleSelect) {
						this.form.controls[filter.id].setValue(filter.preservedValues);
					} else {
						this.form.controls[filter.id].setValue(filter.preservedValues[0]);
					}
				}
			} else {
				if (filter.preservedValues?.length == 1) {
					this.form.controls[filter.id].setValue(filter.preservedValues[0]);
				}
			}
		})
	}

	ngOnInit(): void {
		this.filters.map(filter => {
			if (filter.type === 'date') {
				if (filter.datePickerRange) {
					this.form.setControl(filter.id + 'Start', new FormControl<string>(null, filter.required ? Validators.required : null));
					this.form.setControl(filter.id + 'End', new FormControl<string>(null, filter.required ? Validators.required : null));
					if (this.notEditable || filter.disabled) {
						this.form.controls[filter.id + 'Start'].disable();
						this.form.controls[filter.id + 'End'].disable();
					}
				} else {
					this.form.setControl(filter.id, new FormControl<string>(null, filter.required ? Validators.required : null));
					if (this.notEditable || filter.disabled) {
						this.form.controls[filter.id].disable();
					}
				}
			} else if (['select', 'autocomplete'].includes(filter.type)) {
				if (filter.multipleSelect) {
					this.form.setControl(filter.id, new FormControl<string[]>(null, filter.required ? Validators.required : null));
				} else {
					this.form.setControl(filter.id, new FormControl<string>(null, filter.required ? Validators.required : null));
				}
				if (this.notEditable || filter.disabled) {
					this.form.controls[filter.id].disable();
				}
				if (filter.type === 'autocomplete') {
					this.refModels.set(filter.id, new AutoCompleteRefModel(
						this.form.controls[filter.id],
						(value: string | any) => filter.fetchCallback(value, this.instance)
					));
					this.autocompleteService.addAutocompleteEvent(this.refModels.get(filter.id));
				}
			} else {
				this.form.setControl(filter.id, new FormControl<string>(null, filter.required ? Validators.required : null));
				if (this.notEditable || filter.disabled) {
					this.form.controls[filter.id].disable();
				}
			}
		});
		this.setDefaultValues();
		this.setPreservedValues();
	}

	ngAfterViewInit(): void {
		if (this.groupValidators?.length) {
			this.form.addValidators(this.groupValidators);
			this.form.reset();
		}
		this.setDefaultValues();
		this.setPreservedValues();
		if (!this.isExpansionOpen) {
			this.matExpansionPanel.close();
		}
		this.cdref.detectChanges();
	}

	private getSearchCriteria(): any {
		const searchCriteria: any = {};

		Object.keys(this.form.controls).forEach(key => {
			const control = this.form.controls[key] as FormControl;
			searchCriteria[key] = control.value;

			let filter;
			if (key.includes('Start') || key.includes('End')) {
				filter = this.filters.find(filter => filter.id === key.replace('Start', '').replace('End', ''));
			} else {
				filter = this.filters.find(filter => filter.id === key);
				if (filter.type !== 'date') {
					filter = undefined;
				}
			}
			if (filter) {
				if (searchCriteria[key]) {
					searchCriteria[key] = new Date(searchCriteria[key]);
					if (key.includes('End')) {
						searchCriteria[key] = UtilsService.dateToServerDate(searchCriteria[key]);
					} else {
						searchCriteria[key] = UtilsService.dateToServerDate(searchCriteria[key]);
					}
				}
			}
		});

		return searchCriteria;
	}

	clear() {
		this.autocompleteDropdowns.forEach((autocomplete: SelectAutocompleteComponent) => autocomplete.clearSearch());
		this.form.reset();
		this.setDefaultValues();

		this.filters.forEach((filter) => filter.preservedValues = undefined);

		if (this.form.valid && this.emittableClear) {
			this.search.emit(this.getSearchCriteria());
		}
	}

	onSearch() {
		if (this.form.valid) {
			this.search.emit(this.getSearchCriteria()).then(() => this.closeExpansionOnAction ? this.matExpansionPanel.close() : null);
		} else {
			this.invalidForm.emit(this.form);
		}
	}

	onExport() {
		if (this.form.valid || this.notEditable) {
			this.export.emit(this.getSearchCriteria()).then(() => this.closeExpansionOnAction ? this.matExpansionPanel.close() : null);
		} else {
			this.invalidForm.emit(this.form);
		}
	}

	doCustomAction(button: CustomFilterButton) {
		button.action(this.getSearchCriteria(), this.instance);
	}

	emitChange(event: any, filterId: any) {
		this.filterChange.emit({ event, filterId }).then((filters: any) => {
			filters?.forEach((filter: any) => {
				this.autocompleteService.resetAutocomplete(this.refModels.get(filter));
			})
		});
	}

	emitClear(filterId: any) {
		this.filterClear.emit({ filterId }).then((filters: any) => {
			filters?.forEach((filter: any) => {
				this.autocompleteService.resetAutocomplete(this.refModels.get(filter));
			})
		})
	}

	buildGuardParamRole(button: CustomFilterButton) {
		let customRoles: string[] = [];
		if (button.roles?.length) {
			customRoles = button.roles;
		}
		return {
			customRole: customRoles,
			path: '',
		} as RoleBasedParamModel;
	}

	buildGuardParamBasedRole(): RoleBasedParamModel {
		// Add custom AD role
		let customRoles: string[] = [];
		return {
			customRole: customRoles,
			path: '',
		} as RoleBasedParamModel;
	}
}
